import { Autocomplete, Dialog, DialogContent } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { Formik } from "formik";
import React from "react";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import { COMPANY_PERMISSIONS } from "@/constants/modules.permissions";
import useHasNoPermission from "@/hooks/use-check-permissions";
import { IServiceHours } from "@/interfaces/service-hours.interface";
import { ConfigurationTabMessages } from "@/messages/customer/configuration-tab.messages";

import {
  buildServiceHoursFormSchema,
  buildServiceHoursInitialData,
  mapServiceHoursFormToInput,
} from "./form-data";
import { WEEK_DAY_OPTIONS } from "../../constants";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  messages: ConfigurationTabMessages;
  isLoading: boolean;
  selected?: IServiceHours;
  onSubmit: (values: ReturnType<typeof mapServiceHoursFormToInput>) => void;
};

function ServiceHoursDialog({ isOpen, onClose, messages, isLoading, selected, onSubmit }: Props) {
  const hasNoPermissionToEdit = useHasNoPermission(COMPANY_PERMISSIONS.EDIT);
  const weekDayOptions = WEEK_DAY_OPTIONS(messages);
  const isEditing = !!selected;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent>
        <Formik
          enableReinitialize
          initialValues={buildServiceHoursInitialData(messages, selected)}
          validationSchema={buildServiceHoursFormSchema(messages)}
          onSubmit={(values) => onSubmit(mapServiceHoursFormToInput(values, messages))}
        >
          {({ values, errors, handleChange, setFieldValue, submitForm }) => (
            <MDBox p={2}>
              <MDTypography variant="h5" fontWeight="bold" mb={3}>
                {isEditing ? messages.serviceHoursFormEditTitle : messages.serviceHoursFormCreateTitle}
              </MDTypography>

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Autocomplete
                    fullWidth
                    options={weekDayOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    value={weekDayOptions.find((option) => option.value === values.weekDay) || null}
                    onChange={(_, newValue) => {
                      setFieldValue("weekDay", newValue?.value ?? null);
                      if (newValue && !values.description) {
                        setFieldValue("description", newValue.label);
                      }
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label={messages.serviceHoursWeekDayLabel}
                        variant="standard"
                        error={!!errors.weekDay}
                        required
                      />
                    )}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <FormField
                    name="description"
                    label={messages.serviceHoursDescriptionLabel}
                    value={values.description}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.description}
                    required
                    variant="standard"
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TimePicker
                    label={messages.serviceHoursStartHourLabel}
                    value={values.startOperationHour}
                    onChange={(value) => setFieldValue("startOperationHour", value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "standard",
                        error: !!errors.startOperationHour,
                        required: true,
                      },
                    }}
                  />
                </Grid>

                <Grid size={{ xs: 12, md: 6 }}>
                  <TimePicker
                    label={messages.serviceHoursEndHourLabel}
                    value={values.endOperationHour}
                    onChange={(value) => setFieldValue("endOperationHour", value)}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        variant: "standard",
                        error: !!errors.endOperationHour,
                        required: true,
                      },
                    }}
                  />
                </Grid>
              </Grid>

              <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
                <MDButton variant="outlined" color="dark" onClick={onClose}>
                  {messages.serviceHoursCancelButton}
                </MDButton>
                <MDButton
                  variant="gradient"
                  color="dark"
                  onClick={submitForm}
                  isLoading={isLoading}
                  disabled={hasNoPermissionToEdit}
                >
                  {messages.serviceHoursSaveButton}
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default ServiceHoursDialog;
